import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmt, fmtMoney, formatErr, invalidateSaleQueries } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inputCls, labelCls, btnPrimary, btnAccent, btnOutline } from "@/components/Modal";
import NumberInput from "@/components/NumberInput";
import { Search, Plus, Trash2, Printer, Download } from "lucide-react";
import { toast } from "sonner";
import Modal from "@/components/Modal";
import { downloadInvoice, previewInvoice } from "@/lib/pdf";

export default function POS() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canBackdate = user?.role === "admin" || user?.role === "manager";
  const today = new Date().toLocaleDateString("en-CA");   // YYYY-MM-DD, local
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState([]); // {product, qty, discount}
  const [billDiscount, setBillDiscount] = useState(0);
  const [method, setMethod] = useState("cash");
  const [clientId, setClientId] = useState("");
  const [paidAmount, setPaidAmount] = useState(0);
  const [saleDate, setSaleDate] = useState(today);
  const [invoice, setInvoice] = useState(null);

  const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: () => api.get("/products").then((r) => r.data) });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => api.get("/clients").then((r) => r.data) });

  const filtered = useMemo(() => {
    if (!search) return products.slice(0, 12);
    const s = search.toLowerCase();
    return products.filter((p) => p.name.toLowerCase().includes(s) || p.sku.toLowerCase().includes(s)).slice(0, 20);
  }, [products, search]);

  const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

  const subtotal = cart.reduce((sum, c) => sum + (num(c.qty) * c.product.selling_price - num(c.discount)), 0);
  const total = Math.max(0, subtotal - (parseFloat(billDiscount) || 0));

  const addToCart = (p) => {
    if (p.stock_qty <= 0) return toast.error("Out of stock");
    setCart((prev) => {
      const existing = prev.find((c) => c.product.id === p.id);
      if (existing) {
        if (num(existing.qty) + 1 > p.stock_qty) { toast.error("Stock limit reached"); return prev; }
        return prev.map((c) => c.product.id === p.id ? { ...c, qty: num(c.qty) + 1 } : c);
      }
      return [...prev, { product: p, qty: 1, discount: 0 }];
    });
  };

  // Keep the raw typed value while editing so the field can be emptied/retyped;
  // normalise to a valid number only when the field loses focus.
  const updateQty = (id, v) => setCart((p) => p.map((c) => c.product.id === id ? { ...c, qty: v } : c));
  const updateDiscount = (id, v) => setCart((p) => p.map((c) => c.product.id === id ? { ...c, discount: v } : c));
  const blurQty = (id) => setCart((p) => p.map((c) => {
    if (c.product.id !== id) return c;
    let q = num(c.qty);
    if (q <= 0) q = 1;
    if (q > c.product.stock_qty) { q = c.product.stock_qty; toast.error("Stock limit reached"); }
    return { ...c, qty: q };
  }));
  const blurDiscount = (id) => setCart((p) => p.map((c) => c.product.id === id ? { ...c, discount: Math.max(0, num(c.discount)) } : c));
  const removeItem = (id) => setCart((p) => p.filter((c) => c.product.id !== id));

  const create = useMutation({
    mutationFn: () => api.post("/sales", {
      items: cart.map((c) => ({ product_id: c.product.id, quantity: num(c.qty), discount: num(c.discount) })),
      discount: parseFloat(billDiscount) || 0,
      payment_method: method,
      paid_amount: method === "partial" ? parseFloat(paidAmount) || 0 : 0,
      client_id: (method === "credit" || method === "partial") ? clientId : null,
      date: canBackdate && saleDate && saleDate !== today ? saleDate : undefined,
    }).then((r) => r.data),
    onSuccess: (sale) => {
      toast.success(`Sale recorded: ${sale.invoice_no}`);
      setInvoice(sale);
      setCart([]); setBillDiscount(0); setPaidAmount(0); setClientId(""); setMethod("cash"); setSaleDate(today);
      invalidateSaleQueries(qc);
    },
    onError: (e) => toast.error(formatErr(e.response?.data?.detail)),
  });

  const canCheckout = cart.length > 0 && ((method === "cash") || ((method === "credit" || method === "partial") && clientId));

  return (
    <div className="space-y-6" data-testid="pos-page">
      <div>
        <div className="label-cap">Point of Sale</div>
        <h1 className="font-heading text-3xl mt-1">New Sale</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Product browser */}
        <div className="lg:col-span-3 space-y-4">
          <div className="card-soft p-4">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-agri-ink-2" />
              <input data-testid="pos-search-input" autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search products by name or SKU…" className={inputCls + " pl-9"} />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="pos-product-grid">
            {filtered.map((p) => (
              <button key={p.id} data-testid={`pos-product-${p.id}`} onClick={() => addToCart(p)} disabled={p.stock_qty <= 0}
                className="card-soft p-4 text-left hover:border-agri-forest hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                <div className="text-[10px] uppercase tracking-wider text-agri-ink-2">{p.category}</div>
                <div className="font-medium text-sm text-agri-ink mt-1 line-clamp-2">{p.name}</div>
                <div className="mt-2 flex items-baseline justify-between">
                  <div className="text-agri-forest font-bold">{fmtMoney(p.selling_price)}</div>
                  <div className="text-xs text-agri-ink-2">{fmt(p.stock_qty)} {p.unit}</div>
                </div>
              </button>
            ))}
            {filtered.length === 0 && <div className="col-span-full text-center text-sm text-agri-ink-2 py-8">No products match.</div>}
          </div>
        </div>

        {/* Cart */}
        <div className="lg:col-span-2">
          <div className="card-soft p-5 sticky top-20">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-xl">Cart</h3>
              <span className="text-xs text-agri-ink-2">{cart.length} item(s)</span>
            </div>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto -mr-1 pr-1" data-testid="pos-cart">
              {cart.length === 0 && <div className="text-sm text-agri-ink-2 py-6 text-center">Cart is empty.</div>}
              {cart.map((c) => (
                <div key={c.product.id} className="border border-[rgba(28,28,30,0.06)] rounded-lg p-2.5">
                  <div className="flex justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{c.product.name}</div>
                      <div className="text-xs text-agri-ink-2">{fmtMoney(c.product.selling_price)} / {c.product.unit}</div>
                    </div>
                    <button onClick={() => removeItem(c.product.id)} className="text-agri-ink-2 hover:text-agri-danger"><Trash2 size={14} /></button>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 items-center">
                    <input type="number" step="1" min="0.01" max={c.product.stock_qty} value={c.qty} onFocus={(e) => e.target.select()} onChange={(e) => updateQty(c.product.id, e.target.value)} onBlur={() => blurQty(c.product.id)} className={inputCls + " text-xs py-1"} />
                    <input type="number" step="1" min="0" placeholder="Disc" value={c.discount || ""} onChange={(e) => updateDiscount(c.product.id, e.target.value)} onBlur={() => blurDiscount(c.product.id)} className={inputCls + " text-xs py-1"} />
                    <div className="text-right text-sm font-semibold tabular-nums">{fmtMoney(num(c.qty) * c.product.selling_price - num(c.discount))}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 border-t border-[rgba(28,28,30,0.06)] pt-3 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-agri-ink-2">Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
              <div className="flex justify-between items-center gap-2">
                <span className="text-agri-ink-2">Bill discount</span>
                <NumberInput value={billDiscount} onChange={setBillDiscount} className="w-28 py-1 text-right" />
              </div>
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-[rgba(28,28,30,0.06)]">
                <span>Total</span><span className="text-agri-forest">{fmtMoney(total)}</span>
              </div>
            </div>

            <div className="mt-4 space-y-3">
              {canBackdate && (
                <div>
                  <label className={labelCls}>Sale date</label>
                  <input type="date" data-testid="pos-date-input" value={saleDate} max={today}
                    onChange={(e) => setSaleDate(e.target.value || today)} className={inputCls} />
                  {saleDate !== today && <div className="text-xs text-agri-amber-dark mt-1">Back-dated to {new Date(saleDate).toLocaleDateString()}</div>}
                </div>
              )}
              <div>
                <label className={labelCls}>Payment method</label>
                <div className="grid grid-cols-3 gap-2">
                  {["cash", "credit", "partial"].map((m) => (
                    <button key={m} data-testid={`pos-method-${m}`} type="button" onClick={() => setMethod(m)}
                      className={`text-xs font-medium px-3 py-2 rounded-lg border transition-colors capitalize ${
                        method === m ? "bg-agri-forest text-white border-agri-forest" : "bg-white border-[rgba(28,28,30,0.12)] text-agri-ink hover:bg-agri-bg"
                      }`}>{m}</button>
                  ))}
                </div>
              </div>
              {(method === "credit" || method === "partial") && (
                <div>
                  <label className={labelCls}>Client</label>
                  <select data-testid="pos-client-select" value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls}>
                    <option value="">Select client…</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.area}</option>)}
                  </select>
                </div>
              )}
              {method === "partial" && (
                <div>
                  <label className={labelCls}>Amount received</label>
                  <NumberInput value={paidAmount} max={total} onChange={setPaidAmount} />
                  <div className="text-xs text-agri-ink-2 mt-1">Credit: {fmtMoney(Math.max(0, total - (parseFloat(paidAmount) || 0)))}</div>
                </div>
              )}
              <button data-testid="pos-checkout-btn" disabled={!canCheckout || create.isPending} onClick={() => create.mutate()} className={btnAccent + " w-full disabled:opacity-50"}>
                {create.isPending ? "Processing…" : `Complete sale · ${fmtMoney(total)}`}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal open={!!invoice} onClose={() => setInvoice(null)} title={`Invoice ${invoice?.invoice_no || ""}`} size="md" testId="invoice-modal">
        {invoice && (
          <div className="space-y-4 text-sm">
            <div className="flex justify-between text-xs text-agri-ink-2">
              <div>Date: {new Date(invoice.created_at).toLocaleString()}</div>
              <div>By: {invoice.salesperson_name}</div>
            </div>
            {invoice.client_name && <div>Client: <strong>{invoice.client_name}</strong></div>}
            <table className="w-full text-sm border-t border-b border-[rgba(28,28,30,0.06)]">
              <thead><tr className="text-xs uppercase text-agri-ink-2"><th className="py-2 text-left">Item</th><th className="text-right">Qty</th><th className="text-right">Total</th></tr></thead>
              <tbody>
                {invoice.items.map((it, i) => <tr key={i}><td className="py-1.5">{it.product_name}</td><td className="text-right">{it.quantity}</td><td className="text-right">{fmtMoney(it.line_total)}</td></tr>)}
              </tbody>
            </table>
            <div className="space-y-1 text-right">
              <div>Subtotal: {fmtMoney(invoice.subtotal)}</div>
              {invoice.discount > 0 && <div>Discount: -{fmtMoney(invoice.discount)}</div>}
              <div className="text-lg font-bold text-agri-forest">Total: {fmtMoney(invoice.total)}</div>
              <div className="text-xs text-agri-ink-2">Paid: {fmtMoney(invoice.paid_amount)} · Credit: {fmtMoney(invoice.credit_amount)}</div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => previewInvoice(invoice)} data-testid="invoice-preview-btn" className={btnOutline + " flex items-center gap-2"}><Printer size={14} /> Preview / Print</button>
              <button onClick={() => downloadInvoice(invoice)} data-testid="invoice-download-btn" className={btnOutline + " flex items-center gap-2"}><Download size={14} /> Download PDF</button>
              <button onClick={() => setInvoice(null)} className={btnPrimary}>Done</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
